import { useStore } from '@nanostores/react';
import { AccountFeatureFlag, accountHasFeatureFlag } from '@/services/auth/feature-flags';
import { accountStore } from '@/services/auth/store';
import type { AugmentedAccount } from '@/services/auth/types';
import {
  hasSignupPublicFlag,
  SignupPublicFeatureFlag,
  signupPublicFlagsStore,
  type SignupPublicFlags,
} from '@/services/signup/public-flags-store';

type RouteOwner = 'hydra' | 'embedded';

/** The slice of the account that route ownership depends on. */
export type RouteOwnershipAccount = Pick<AugmentedAccount, 'feature_flags'> | null;

type RouteOwnershipPattern = {
  owner: RouteOwner;
  pattern: RegExp;
  /**
   * When set, the entry only applies while the account feature flag is on — a rollout
   * kill-switch: flag off, and the URL falls through to the default `embedded` owner. Before
   * the account loads, the flag reads its default (off ⇒ embedded), so any consumer that
   * evaluates ownership during render and caches the answer MUST re-evaluate once the
   * account arrives — use `useRouteOwner`/`useIsHydraRoute` for that.
   */
  flag?: AccountFeatureFlag;
  /**
   * Like `flag`, but gated on a *public* feature flag — the rollout mechanism
   * for pre-auth routes, where no account exists to carry an account flag.
   * While the public-flags fetch hasn't settled (a `null` store), gated
   * entries don't apply (⇒ embedded), mirroring the account-flag cold-load
   * behavior above. Render-time consumers are covered by the route gate's
   * loader; call-time consumers (the `window.__hydra__` bridge, navigation,
   * active-route-sync) instead rely on the eager pre-auth prefetch in
   * `utils/inject-globals.ts`, which resolves the store before any embedded app
   * can ask about `/signup`. Signup-specific on purpose: rollout bucketing uses
   * the signup-prefixed anonymous id, so this gate must not be casually reused
   * for non-signup routes.
   */
  signupPublicFlag?: SignupPublicFeatureFlag;
};

/**
 * Canonical route ownership map for URLs that cross the Hydra shell /
 * embedded-app boundary.
 *
 * TanStack owns exact route matching, but embedded apps still ask Hydra whether
 * a browser URL should stay in the shell. Keep those ownership decisions here
 * so active-route sync, global navigation helpers, and Ember interop do not
 * each grow their own handwritten route model.
 *
 * Entries with a `flag` are gated on an account feature flag (via `hasFeatureFlag`,
 * this module's only runtime data dependency) — every consumer, including the
 * `window.__hydra__.isHydraRoute` bridge Ember calls, reads the answer fresh per
 * navigation, so a flag flip propagates without any consumer changes.
 */
const ROUTE_OWNERSHIP_PATTERNS: Array<RouteOwnershipPattern> = [
  // Auth-entry routes migrated to Hydra (FPS-2341): login + its sub-steps,
  // logout, academy-login, and the legacy /users/login redirect.
  { owner: 'hydra', pattern: /^\/login(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/logout(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/academy-login(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/users\/login(?:[#/?]|$)/i },
  // Signup funnel (FPS-2851): the whole /signup tree plus its legacy
  // /users/register and /users/signup aliases, dark behind the
  // `signup-migration` rollout flag. The funnel is pre-auth, so the gate is a
  // public feature flag (resolvable for anonymous visitors); flag off, the
  // URLs fall back to the embedded app, whose signup routes are intact.
  // `SignupRouteGate` renders the embedded host on the same flag so the
  // ownership answer and the rendered app always agree.
  {
    owner: 'hydra',
    pattern: /^\/signup(?:[#/?]|$)/i,
    signupPublicFlag: SignupPublicFeatureFlag.SignupMigration,
  },
  {
    owner: 'hydra',
    pattern: /^\/users\/(register|signup)(?:[#/?]|$)/i,
    signupPublicFlag: SignupPublicFeatureFlag.SignupMigration,
  },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/ai-business-profile/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/audit-logs/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/api-keys/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/api_credentials/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/auto_archive/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/edit/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/confirm_delete/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/geofences/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/merge_options/i },
  // LLM configuration (FPS-2355): the whole route set — index, /new, and
  // /:id/edit — is Hydra-owned.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/llm_configuration/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/language/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/anonymous_events/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/suppressions/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/actions\/sms-providers/i },
  // Webhook configuration (FPS-2354): the whole subtree — index list, `/new`,
  // and `/:id/edit`.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/settings\/webhook_configuration(?:\/|$|[#?])/i,
  },
  { owner: 'hydra', pattern: /^\/settings\/ai(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/settings\/account(?:[#/?]|$)/i },
  // Partner used to live under /settings/account/partner (covered by the
  // /settings/account pattern above); now it's a top-level settings path.
  { owner: 'hydra', pattern: /^\/settings\/partner(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/settings\/personal\/google-identities(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/settings\/roles(?:[#/?]|$)/i },
  // Team members: the index (list), `/add` (invite), `/members/:id/edit`
  // (FPS-2537), `/invite-guest` (FPS-2557), and `/join-requests/:id` (the approve
  // page) are Hydra-owned. `/set_password` is still Ember, so this matches only
  // those five shapes and leaves it to hand off to Ember. Widen to the full
  // subtree once set_password migrates.
  {
    owner: 'hydra',
    pattern: /^\/settings\/team(?:\/add|\/invite-guest|\/members\/[^/]+\/edit|\/join-requests\/[^/]+)?\/?(?:[#?]|$)/i,
  },
  // The workspaces list and create (/new) are both Hydra-owned now.
  { owner: 'hydra', pattern: /^\/settings\/workspaces(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/settings\/privacy/i },
  { owner: 'hydra', pattern: /^\/settings\/api-keys/i },
  { owner: 'hydra', pattern: /^\/settings\/api_credentials/i },
  { owner: 'hydra', pattern: /^\/settings\/audit-logs/i },
  { owner: 'hydra', pattern: /^\/settings\/experimental-features/i },
  // Security settings (FPS-2223), fully rolled out at the FPS-2767 cutover: no flag gate,
  // and the Ember pods are deleted. Matches ONLY the two real routes — the index and the
  // nested `/sso` page (each with an optional trailing slash, query, or hash). Deliberately
  // not the whole subtree: there is no layout or splat route under `/settings/security`, so
  // an unknown nested URL resolves to the `settings/$` splat and renders the embedded app.
  // Claiming it here would make ownership say `hydra` while Ember is on screen — and after
  // the teardown Ember cannot route it either. Widen only alongside a real Hydra route.
  { owner: 'hydra', pattern: /^\/settings\/security(?:\/sso)?\/?(?:[#?]|$)/i },
  // Plans & Billing (SPE-3782). The whole `/settings/billing` subtree — shell plus every
  // tab — is Hydra-owned as one unit, dark behind the `hydra-billing` kill-switch until the
  // FlightDeck rollout: flag off, the URLs fall back to the embedded Ember app (whose billing
  // routes are intact). `BillingRoute` renders the Ember host on the same flag so the
  // ownership answer and the rendered app always agree, and the flag flips ownership of all
  // tabs atomically (never split per-tab).
  {
    owner: 'hydra',
    pattern: /^\/settings\/billing(?:\/|$|[#?])/i,
    flag: AccountFeatureFlag.HydraBilling,
  },
  // `/settings/receipts` (SPE-3769) redirects into billing history, owned by Hydra on the same
  // billing flag. Match only the bare path — `/settings/receipts/:id` stays embedded (the
  // server-rendered Stripe receipt), so it must not be captured here.
  {
    owner: 'hydra',
    pattern: /^\/settings\/receipts(?:$|[#?])/i,
    flag: AccountFeatureFlag.HydraBilling,
  },
  // Promo checkout page (SPE-3891). The top-level, chrome-less `/checkout` page is outside the
  // billing tab area but part of the billing migration, so it's Hydra-owned behind the same
  // `hydra-billing` flag (removed together at teardown). Flag off, the URL falls back to the
  // embedded Ember app. `PromoCheckoutRoute` renders the Ember host on the same flag so the
  // ownership answer and the rendered app agree.
  {
    owner: 'hydra',
    pattern: /^\/checkout(?:\/|$|[#?])/i,
    flag: AccountFeatureFlag.HydraBilling,
  },
  // Essentials onboarding (SELF-138): the chrome-less /onboarding pages. Net-new
  // Hydra routes with no embedded equivalent, so the entry is UNGATED on purpose —
  // gating ownership on `essentials_onboarding` would hand flag-off traffic to
  // the embedded app, which can only boot into a 404. Flag-off users get
  // Hydra's NotFoundPage from the route layout's FeatureFlagGuard instead.
  // Verified against customerio/ui's router.js: no URL space under /onboarding
  // exists there — the only "onboarding" route names are the signup children
  // (/signup/onboarding-usage, /signup/onboarding-personalize), which this
  // pattern does not touch. CDP and Design Studio bundles never receive
  // top-level URLs (getAppBundleFromUrl routes those by their own prefixes).
  { owner: 'hydra', pattern: /^\/onboarding(?:\/|$|[#?])/i },
  { owner: 'hydra', pattern: /^\/har-sanitizer/i },
  // Guest-invitation accept page: Hydra-owned. Reached from an email link; the Ember route is
  // retained only as a revert target until the teardown.
  { owner: 'hydra', pattern: /^\/accept-guest-invitation(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/no_accessible_workspaces/i },
  { owner: 'hydra', pattern: /^\/post_cancel/i },
  { owner: 'hydra', pattern: /^\/confirm_account_notice/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/agent/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/tasks/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/analysis\/deliverability/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/analysis\/email-clients/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/health/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/home/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/pipelines/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/geosets/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/geofences/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/message-library/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/message-library/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/snippets/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/snippets/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/assets/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/assets/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/(journeys\/)?exports/i },
  // Collections (SPE-3918), dark behind the `collections_in_hydra` kill-switch:
  // flag off, these URLs fall back to the embedded Ember app. This intentionally
  // claims the index, bare detail, and data routes only; importing/usage stay
  // Ember-owned until their Hydra equivalents exist.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/collections(?:\/[^/?#]+(?:\/data)?)?\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.CollectionsInHydra,
  },
  // Activity logs (FPS-2304). Both taxonomy segments are Hydra-owned; the future
  // `profile_activity` segment redirects to the canonical `activity_logs` page.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/activity_logs/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/profile_activity/i },
  // Outbox tabs (FPS-2305), across both taxonomy segments (`outbox` current, `message_activity`
  // future). Dark behind the `outbox-migration` kill-switch: flag off, the URLs fall back to the
  // embedded Ember app; `OutboxRouteView` renders the Ember host on the same flag so ownership
  // and the rendered app agree. Migrated: the Deliveries/Sent tab + its `/:deliveryId` detail,
  // and the Drafts tab **list**. The draft detail/edit (`/drafts/:id`…) and any deeper path
  // aren't migrated, so they fall through to `embedded`.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/outbox\/(?:deliveries(?:\/[^/]+)?|drafts)\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.OutboxMigration,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/message_activity\/(?:sent(?:\/[^/]+)?|drafts)\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.OutboxMigration,
  },
  // The entire layouts feature is Hydra-owned now (FPS-2585): list
  // (FPS-2299), starters (FPS-2556), detail (FPS-2571), and create/edit
  // (FPS-2555). Claim the whole `/journeys/layouts` subtree.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/layouts(?:\/|$|[#?])/i },
  // Standalone template dispatcher (FPS-2300). The trailing guard keeps the
  // pattern from claiming Ember's `/journeys/composer/.../templates/...` URLs.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/(journeys\/)?template(?:[#/?]|$)/i },
  // Design Studio dashboard (CON-7495), fully rolled out: the page is native
  // here and the embedded Design Studio bundle no longer carries a dashboard
  // route to fall back to. The pattern matches only the exact dashboard URL
  // (plus optional trailing slash, query, or hash): the native route is a
  // static path with no children, so `/dashboard/<sub>` and `/dashboard-*` URLs
  // route to the `/design-studio/$` splat and must stay with the embedded app —
  // ownership mirrors that.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/design-studio\/dashboard\/?(?:[#?]|$)/i },
  // Design Studio global styles (CON-7588). Ownership is dark behind the
  // `ds-global-styles-in-hydra` kill-switch: flag off, the URL falls back to the
  // embedded Design Studio bundle (whose global-styles route is intact).
  // `DesignStudioGlobalStylesRoute` renders the embedded-app host on the same
  // flag so the ownership answer and the rendered app always agree. The pattern
  // matches only the exact global-styles URL (plus optional trailing slash,
  // query, or hash): the native route is a static path with no children, so
  // `/global-styles/<sub>` and `/global-styles-*` URLs route to the
  // `/design-studio/$` splat and must stay with the embedded app — ownership
  // mirrors that.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/design-studio\/global-styles\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.DsGlobalStylesInHydra,
  },
];

/**
 * Route ownership is a function of the URL *and* the account's feature flags
 * (flag-gated migration entries), so the account is a required input. A null
 * account means "not loaded yet" — flags read their defaults, so flag-gated
 * Hydra routes classify as embedded until the real account is supplied.
 *
 * Call-time consumers (event handlers, the Ember `window.__hydra__` bridge)
 * pass `accountStore.get()`. Render-time consumers must NOT — a component
 * rendered before auth completes would cache the flag-default answer
 *; use `useRouteOwner`/`useIsHydraRoute` instead.
 *
 * Public-flag-gated entries (`/signup`) additionally depend on
 * `signupPublicFlagsStore` being resolved: while it is `null`, the entry reads
 * as `embedded`. For call-time consumers that is guaranteed pre-auth by the
 * eager prefetch in `utils/inject-globals.ts`.
 */
export function getRouteOwner(
  path: string,
  account: RouteOwnershipAccount,
  publicFlags: SignupPublicFlags = signupPublicFlagsStore.get(),
): RouteOwner {
  return (
    ROUTE_OWNERSHIP_PATTERNS.find(
      ({ pattern, flag, signupPublicFlag }) =>
        (flag == null || accountHasFeatureFlag(account, flag)) &&
        (signupPublicFlag == null || hasSignupPublicFlag(publicFlags, signupPublicFlag)) &&
        pattern.test(path),
    )?.owner ?? 'embedded'
  );
}

export function isHydraRoute(
  path: string,
  account: RouteOwnershipAccount,
  publicFlags: SignupPublicFlags = signupPublicFlagsStore.get(),
): boolean {
  return getRouteOwner(path, account, publicFlags) === 'hydra';
}

/**
 * Reactive ownership for render-time consumers: subscribes to the account and
 * the signup public flags so flag-gated entries re-evaluate when either loads.
 */
export function useRouteOwner(path: string): RouteOwner {
  const account = useStore(accountStore);
  const publicFlags = useStore(signupPublicFlagsStore);
  return getRouteOwner(path, account, publicFlags);
}

export function useIsHydraRoute(path: string): boolean {
  return useRouteOwner(path) === 'hydra';
}
